import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FileSpreadsheet,
  LogOut,
  FileText,
  Shield,
  LayoutDashboard,
  AlertTriangle,
  Activity,
  ChevronLeft,
  ChevronRight,
  Server,
  Table2,
  History,
  ClipboardList,
  LayoutGrid,
  SlidersHorizontal,
  UserPlus,
  Users,
  Settings,
  Bot,

  ListChecks,
  ScrollText,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext.tsx';
import { TENANT_PATHS } from '../routing/paths.ts';
import {
  useMaterialityLeaveGuard,
  useMaterialitySignOutWithLeaveGuard,
} from '../contexts/MaterialityLeaveGuardContext.tsx';
import AuditYearSelector from './AuditYearSelector.tsx';
import { isReportingEnabled } from './ReportingRoute.tsx';
import { useOrgModuleAccess } from '../hooks/useOrgModuleAccess.ts';
import { MODULE_KEYS, filterTabsByModuleAccess } from '../constants/moduleAccess.ts';
import { useAIEvents } from '../hooks/useAIEvents.ts';
import { useAiLogOverride } from '../shared/hooks/useAiLogOverride.ts';

function getStoredOrganizationId() {
  if (typeof window === 'undefined') return '';
  return window.localStorage.getItem('organization_id') ?? '';
}

function Sidebar({ user, onSignOut, onCollapseChange }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasUnsavedProgress, confirmLeaveIfNeeded } = useMaterialityLeaveGuard();
  const { unreadCount: aiUnreadCount } = useAIEvents();

  const [showDropdown, setShowDropdown] = useState(false);
  const closeUserDropdown = useCallback(() => setShowDropdown(false), []);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const dropdownRef = useRef(null);
  const { logoUrl } = useTheme();

  // Notify parent component when collapse state changes
  useEffect(() => {
    if (onCollapseChange) {
      onCollapseChange(isCollapsed);
    }
  }, [isCollapsed, onCollapseChange]);

  const { data: moduleAccess, isSuccess } = useOrgModuleAccess();
  const organizationId = getStoredOrganizationId();
  const isOrgAdmin = moduleAccess?.role === 'org_admin';
  // Live from module-access API (not stale login localStorage).
  const aiLogOverride = useAiLogOverride();
  // Own AI keys, or the temporary ?ailog=enable switch for this tab.
  const orgHasAIIntegration = Boolean(moduleAccess?.has_own_ai_credentials) || aiLogOverride;

  // Reporting is enabled when the platform flag is on OR the user has a local preview override.
  const ENABLE_REPORTING = isReportingEnabled(moduleAccess);
  /** Wait for module-access API before showing nav (avoids full-sidebar flash while loading). */
  const navReady = !organizationId || isSuccess;

  const module1Tabs = useMemo(
    () => {
      const tabs = filterTabsByModuleAccess(
        [
          { path: TENANT_PATHS.MATERIALITY_DASHBOARD, label: 'Dashboard', icon: LayoutDashboard, moduleKey: MODULE_KEYS.MATERIALITY },
          { path: TENANT_PATHS.MATERIALITY, label: 'Risk Assessment', icon: AlertTriangle, moduleKey: MODULE_KEYS.MATERIALITY },
          { path: TENANT_PATHS.SOX_MAPPING_REPORT, label: 'Assessment Reports', icon: FileText, moduleKey: MODULE_KEYS.MATERIALITY },
        ],
        moduleAccess
      );
      return navReady ? tabs : [];
    },
    [moduleAccess, navReady]
  );

  const module2Tabs = useMemo(
    () => {
      const tabs = filterTabsByModuleAccess(
        [
          { path: TENANT_PATHS.SOX_DASHBOARD, label: 'Process Charts', icon: Activity, moduleKey: MODULE_KEYS.PROCESSES },
          { path: TENANT_PATHS.SOX_RISKS, label: 'Risks', icon: AlertTriangle, moduleKey: MODULE_KEYS.RISKS },
          { path: TENANT_PATHS.SOX_CONTROLS, label: 'Controls', icon: Shield, moduleKey: MODULE_KEYS.CONTROLS },
          { path: '/risk-control-matrix', label: 'Risk Control Matrix', icon: Table2, moduleKeys: [MODULE_KEYS.RISKS, MODULE_KEYS.CONTROLS] },
          { path: TENANT_PATHS.SOX_SYSTEMS, label: 'Systems', icon: Server, moduleKey: MODULE_KEYS.SYSTEMS },
          { path: TENANT_PATHS.SOX_REPORTS, label: 'Documents & Reports', icon: FileText, moduleKey: MODULE_KEYS.REPORTS },
          { path: TENANT_PATHS.SOP_SYNTHESIZER, label: 'SOP Synthesizer', icon: ScrollText, moduleKey: MODULE_KEYS.SOP_SYNTHESIZER },
        ],
        moduleAccess
      );
      return navReady ? tabs : [];
    },
    [moduleAccess, navReady]
  );

  const module3Tabs = useMemo(
    () => {
      const tabs = filterTabsByModuleAccess(
        [{ path: TENANT_PATHS.VALIDATE, label: 'Validate SOF', icon: FileSpreadsheet, moduleKey: MODULE_KEYS.SOF }],
        moduleAccess
      );
      return navReady ? tabs : [];
    },
    [moduleAccess, navReady]
  );

  const module4Tabs = useMemo(
    () => {
      const tabs = filterTabsByModuleAccess(
        [
          { path: TENANT_PATHS.CONTROL_TESTING_TEST_RUNS, label: 'Test Runs', icon: History, moduleKey: MODULE_KEYS.TEST_EXECUTIONS },
          { path: TENANT_PATHS.ISSUES_MANAGEMENT, label: 'Issue Management', icon: ClipboardList, moduleKey: MODULE_KEYS.SOX_ISSUES },
        ],
        moduleAccess
      );
      return navReady ? tabs : [];
    },
    [moduleAccess, navReady]
  );

  // Readiness: Hygiene Check covers every module, so it shows when the user can open any of them.
  const readinessTabs = useMemo(
    () => {
      const tabs = filterTabsByModuleAccess(
        [
          {
            path: TENANT_PATHS.HYGIENE_CHECK,
            label: 'Hygiene Check',
            icon: ListChecks,
            moduleKeys: [
              MODULE_KEYS.MATERIALITY, MODULE_KEYS.PROCESSES, MODULE_KEYS.RISKS, MODULE_KEYS.CONTROLS,
              MODULE_KEYS.SYSTEMS, MODULE_KEYS.TEST_EXECUTIONS, MODULE_KEYS.SOX_ISSUES, MODULE_KEYS.REPORTS,
            ],
          },
        ],
        moduleAccess
      );
      return navReady ? tabs : [];
    },
    [moduleAccess, navReady]
  );

  const module5Tabs = useMemo(
    () => {
      const tabs = filterTabsByModuleAccess(
        [
          { path: TENANT_PATHS.ANALYTICS_DASHBOARD, label: 'Dashboard', icon: LayoutGrid, moduleKey: MODULE_KEYS.REPORTS },
        ],
        moduleAccess
      );
      return navReady ? tabs : [];
    },
    [moduleAccess, navReady]
  );
  const showWidgetSettings =
    ENABLE_REPORTING && module5Tabs.some((tab) => tab.path === TENANT_PATHS.ANALYTICS_DASHBOARD);

  // Get user initial
  const getUserInitial = () => {
    if (!user?.username) return 'U';
    return user.username.charAt(0).toUpperCase();
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (path) => {
    return location.pathname === path;
  };

  const needsMaterialityLeaveConfirm = useMemo(
    () => hasUnsavedProgress && location.pathname === TENANT_PATHS.MATERIALITY,
    [hasUnsavedProgress, location.pathname]
  );

  /** Block leaving Risk Assessment (/materiality) after files are uploaded — sidebar links + sign out. */
  const onNavigateFromMateriality = useCallback(
    (e, targetPath) => {
      if (
        needsMaterialityLeaveConfirm &&
        targetPath !== TENANT_PATHS.MATERIALITY
      ) {
        e.preventDefault();
        confirmLeaveIfNeeded(() => navigate(targetPath));
      }
    },
    [needsMaterialityLeaveConfirm, confirmLeaveIfNeeded, navigate]
  );

  const handleSignOutClick = useMaterialitySignOutWithLeaveGuard(
    confirmLeaveIfNeeded,
    needsMaterialityLeaveConfirm,
    onSignOut,
    closeUserDropdown
  );

  return (
    <div className={`${isCollapsed ? 'w-16' : 'w-56'} bg-blue-800 h-screen flex flex-col fixed left-0 top-0 transition-all duration-300 ease-in-out`}>
      {/* Logo Section */}
      <div className="p-4">
        <div className={`flex items-center ${isCollapsed ? 'justify-center' : ''}`}>
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Organization logo"
              className="h-8 w-auto object-contain bg-white rounded-md p-1"
            />
          ) : (
            <>
              <div className="bg-white rounded-lg p-2 flex-shrink-0">
                <Activity className="h-6 w-6 text-blue-800" />
              </div>
              {!isCollapsed && (
                <div className="ml-3 transition-opacity duration-300">
                  <h1 className="text-xl font-bold text-white" style={{
                    fontFamily: "'Inter', 'SF Pro Display', -apple-system, sans-serif",
                    letterSpacing: "0.1em",
                    fontWeight: "700",
                  }}>
                    SKEPTICS
                  </h1>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Audit Year Selector — org_admin only */}
      {navReady && isOrgAdmin && !isCollapsed && (
        <div className="px-3 pb-3 border-b border-blue-700">
          <p className="text-xs font-semibold text-blue-300 uppercase tracking-wider mb-2 px-1">
            Audit Year
          </p>
          <AuditYearSelector />
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {!navReady && organizationId && !isCollapsed && (
          <div className="px-3 py-2 text-sm text-blue-200">Loading menu…</div>
        )}

        {/* Module 1 Section */}
        {!isCollapsed && module1Tabs.length > 0 && (
          <div className="px-3 py-2 mb-2">
            <h3 className="text-xs font-semibold text-blue-300 uppercase tracking-wider">
              Risk and Scoping
            </h3>
          </div>
        )}

        {/* Module 1 Navigation */}
        {module1Tabs.map((tab) => {
          const Icon = tab.icon;
          const active = isActive(tab.path);

          return (
            <Link
              key={tab.path + tab.label}
              to={tab.path}
              onClick={(e) => onNavigateFromMateriality(e, tab.path)}
              title={isCollapsed ? tab.label : ''}
              className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} px-3 py-2 mb-1 text-sm font-medium rounded-lg transition-all ${
                active
                  ? 'bg-white text-blue-800'
                  : 'text-blue-100 hover:bg-blue-700 hover:text-white'
              }`}
            >
              <div className={`flex items-center ${isCollapsed ? 'justify-center' : ''}`}>
                <Icon className={`h-5 w-5 flex-shrink-0 ${!isCollapsed ? 'mr-3' : ''}`} />
                {!isCollapsed && <span>{tab.label}</span>}
              </div>
              {!isCollapsed && tab.count && (
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                  active ? 'bg-blue-100 text-blue-800' : 'bg-blue-700 text-blue-100'
                }`}>
                  {tab.count}
                </span>
              )}
            </Link>
          );
        })}

        {/* Module 2 Section */}
        {!isCollapsed && module2Tabs.length > 0 && (
          <div className="px-3 py-2 mb-2 mt-6">
            <h3 className="text-xs font-semibold text-blue-300 uppercase tracking-wider">
              Process Design
            </h3>
          </div>
        )}

        {/* Module 2 Navigation */}
        {module2Tabs.map((tab) => {
          const Icon = tab.icon;
          const active = isActive(tab.path);

          return (
            <Link
              key={tab.path + tab.label}
              to={tab.path}
              onClick={(e) => onNavigateFromMateriality(e, tab.path)}
              title={isCollapsed ? tab.label : ''}
              className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} px-3 py-2 mb-1 text-sm font-medium rounded-lg transition-all ${
                active
                  ? 'bg-white text-blue-800'
                  : 'text-blue-100 hover:bg-blue-700 hover:text-white'
              }`}
            >
              <div className={`flex items-center ${isCollapsed ? 'justify-center' : ''}`}>
                <Icon className={`h-5 w-5 flex-shrink-0 ${!isCollapsed ? 'mr-3' : ''}`} />
                {!isCollapsed && <span>{tab.label}</span>}
              </div>
              {!isCollapsed && tab.count && (
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                  active ? 'bg-blue-100 text-blue-800' : 'bg-blue-700 text-blue-100'
                }`}>
                  {tab.count}
                </span>
              )}
            </Link>
          );
        })}

        {/* Module 4 — Control Testing (above Control Monitoring) */}
        {!isCollapsed && module4Tabs.length > 0 && (
          <div className="px-3 py-2 mb-2 mt-6">
            <h3 className="text-xs font-semibold text-blue-300 uppercase tracking-wider">
              Control Testing
            </h3>
          </div>
        )}

        {module4Tabs.map((tab, index) => {
          const Icon = tab.icon;
          const active = isActive(tab.path);

          return (
            <Link
              key={tab.path + tab.label}
              to={tab.path}
              onClick={(e) => onNavigateFromMateriality(e, tab.path)}
              title={isCollapsed ? tab.label : ''}
              className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} px-3 py-2 mb-1 text-sm font-medium rounded-lg transition-all ${
                isCollapsed && index === 0 ? 'mt-6' : ''
              } ${
                active
                  ? 'bg-white text-blue-800'
                  : 'text-blue-100 hover:bg-blue-700 hover:text-white'
              }`}
            >
              <div className={`flex items-center ${isCollapsed ? 'justify-center' : ''}`}>
                <Icon className={`h-5 w-5 flex-shrink-0 ${!isCollapsed ? 'mr-3' : ''}`} />
                {!isCollapsed && <span>{tab.label}</span>}
              </div>
              {!isCollapsed && tab.count && (
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                  active ? 'bg-blue-100 text-blue-800' : 'bg-blue-700 text-blue-100'
                }`}>
                  {tab.count}
                </span>
              )}
            </Link>
          );
        })}

        {/* Readiness Section — Hygiene Check */}
        {!isCollapsed && readinessTabs.length > 0 && (
          <div className="px-3 py-2 mb-2 mt-6">
            <h3 className="text-xs font-semibold text-blue-300 uppercase tracking-wider">
              Readiness
            </h3>
          </div>
        )}

        {readinessTabs.map((tab, index) => {
          const Icon = tab.icon;
          const active = isActive(tab.path);
          return (
            <Link
              key={tab.path}
              to={tab.path}
              onClick={(e) => onNavigateFromMateriality(e, tab.path)}
              title={isCollapsed ? tab.label : ''}
              className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} px-3 py-2 mb-1 text-sm font-medium rounded-lg transition-all ${
                isCollapsed && index === 0 ? 'mt-6' : ''
              } ${active ? 'bg-white text-blue-800' : 'text-blue-100 hover:bg-blue-700 hover:text-white'}`}
            >
              <div className={`flex items-center ${isCollapsed ? 'justify-center' : ''}`}>
                <Icon className={`h-5 w-5 flex-shrink-0 ${!isCollapsed ? 'mr-3' : ''}`} />
                {!isCollapsed && <span>{tab.label}</span>}
              </div>
            </Link>
          );
        })}

        {/* Module 3 Section — Control Monitoring */}
        {!isCollapsed && module3Tabs.length > 0 && (
          <div className="px-3 py-2 mb-2 mt-6">
            <h3 className="text-xs font-semibold text-blue-300 uppercase tracking-wider">
              Control Monitoring
            </h3>
          </div>
        )}

        {/* Module 3 Navigation */}
        {module3Tabs.map((tab, index) => {
          const Icon = tab.icon;
          const active = isActive(tab.path);

          return (
            <Link
              key={tab.path + tab.label}
              to={tab.path}
              onClick={(e) => onNavigateFromMateriality(e, tab.path)}
              title={isCollapsed ? tab.label : ''}
              className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} px-3 py-2 mb-1 text-sm font-medium rounded-lg transition-all ${
                isCollapsed && index === 0 ? 'mt-6' : ''
              } ${
                active
                  ? 'bg-white text-blue-800'
                  : 'text-blue-100 hover:bg-blue-700 hover:text-white'
              }`}
            >
              <div className={`flex items-center ${isCollapsed ? 'justify-center' : ''}`}>
                <Icon className={`h-5 w-5 flex-shrink-0 ${!isCollapsed ? 'mr-3' : ''}`} />
                {!isCollapsed && <span>{tab.label}</span>}
              </div>
              {!isCollapsed && tab.count && (
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                  active ? 'bg-blue-100 text-blue-800' : 'bg-blue-700 text-blue-100'
                }`}>
                  {tab.count}
                </span>
              )}
            </Link>
          );
        })}

        {/* Module 5 Section — Reporting & Analytics */}
        {ENABLE_REPORTING && (
          <>
            {!isCollapsed && module5Tabs.length > 0 && (
              <div className="px-3 py-2 mb-2 mt-6">
                <h3 className="text-xs font-semibold text-blue-300 uppercase tracking-wider">
                  Reporting &amp; Analytics
                </h3>
              </div>
            )}

            {module5Tabs.map((tab, index) => {
              const Icon = tab.icon;
              const active = isActive(tab.path);

              return (
                <Link
                  key={tab.path + tab.label}
                  to={tab.path}
                  onClick={(e) => onNavigateFromMateriality(e, tab.path)}
                  title={isCollapsed ? tab.label : ''}
                  className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} px-3 py-2 mb-1 text-sm font-medium rounded-lg transition-all ${
                    isCollapsed && index === 0 ? 'mt-6' : ''
                  } ${
                    active
                      ? 'bg-white text-blue-800'
                      : 'text-blue-100 hover:bg-blue-700 hover:text-white'
                  }`}
                >
                  <div className={`flex items-center ${isCollapsed ? 'justify-center' : ''}`}>
                    <Icon className={`h-5 w-5 flex-shrink-0 ${!isCollapsed ? 'mr-3' : ''}`} />
                    {!isCollapsed && <span>{tab.label}</span>}
                  </div>
                  {!isCollapsed && tab.count && (
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      active ? 'bg-blue-100 text-blue-800' : 'bg-blue-700 text-blue-100'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </Link>
              );
            })}
          </>
        )}
        {/* Org Admin — org_admin only */}
        {navReady && isOrgAdmin && !isCollapsed && (
          <div className="px-3 py-2 mb-2 mt-6">
            <h3 className="text-xs font-semibold text-blue-300 uppercase tracking-wider">
              Org Admin
            </h3>
          </div>
        )}
        {navReady && isOrgAdmin && (
          <Link
            to={TENANT_PATHS.SETTINGS_ORG_MEMBERS}
            onClick={(e) => onNavigateFromMateriality(e, TENANT_PATHS.SETTINGS_ORG_MEMBERS)}
            title={isCollapsed ? 'Manage Members' : ''}
            className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} px-3 py-2 mb-1 text-sm font-medium rounded-lg transition-all ${
              isActive(TENANT_PATHS.SETTINGS_ORG_MEMBERS)
                ? 'bg-white text-blue-800'
                : 'text-blue-100 hover:bg-blue-700 hover:text-white'
            }`}
          >
            <div className={`flex items-center ${isCollapsed ? 'justify-center' : ''}`}>
              <Users className={`h-5 w-5 flex-shrink-0 ${!isCollapsed ? 'mr-3' : ''}`} />
              {!isCollapsed && <span>Manage Members</span>}
            </div>
          </Link>
        )}
        {navReady && isOrgAdmin && (
          <Link
            to={TENANT_PATHS.SETTINGS_ORG_USERS}
            onClick={(e) => onNavigateFromMateriality(e, TENANT_PATHS.SETTINGS_ORG_USERS)}
            title={isCollapsed ? 'Create User' : ''}
            className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} px-3 py-2 mb-1 text-sm font-medium rounded-lg transition-all ${
              isActive(TENANT_PATHS.SETTINGS_ORG_USERS)
                ? 'bg-white text-blue-800'
                : 'text-blue-100 hover:bg-blue-700 hover:text-white'
            }`}
          >
            <div className={`flex items-center ${isCollapsed ? 'justify-center' : ''}`}>
              <UserPlus className={`h-5 w-5 flex-shrink-0 ${!isCollapsed ? 'mr-3' : ''}`} />
              {!isCollapsed && <span>Create User</span>}
            </div>
          </Link>
        )}
        {navReady && isOrgAdmin && (
          <Link
            to={TENANT_PATHS.SETTINGS_ORG_AUDIT_LOG}
            onClick={(e) => onNavigateFromMateriality(e, TENANT_PATHS.SETTINGS_ORG_AUDIT_LOG)}
            title={isCollapsed ? 'Audit Log' : ''}
            className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} px-3 py-2 mb-1 text-sm font-medium rounded-lg transition-all ${
              isActive(TENANT_PATHS.SETTINGS_ORG_AUDIT_LOG)
                ? 'bg-white text-blue-800'
                : 'text-blue-100 hover:bg-blue-700 hover:text-white'
            }`}
          >
            <div className={`flex items-center ${isCollapsed ? 'justify-center' : ''}`}>
              <History className={`h-5 w-5 flex-shrink-0 ${!isCollapsed ? 'mr-3' : ''}`} />
              {!isCollapsed && <span>Audit Log</span>}
            </div>
          </Link>
        )}
        {/* Settings Section — Dashboard Widgets for every reporting user; Audit Years for org_admin */}
        {navReady && (isOrgAdmin || showWidgetSettings) && !isCollapsed && (
          <div className="px-3 py-2 mb-2 mt-6">
            <h3 className="text-xs font-semibold text-blue-300 uppercase tracking-wider">
              Settings
            </h3>
          </div>
        )}
        {showWidgetSettings && (
          <Link
            to={TENANT_PATHS.SETTINGS_DASHBOARD_WIDGETS}
            onClick={(e) => onNavigateFromMateriality(e, TENANT_PATHS.SETTINGS_DASHBOARD_WIDGETS)}
            title={isCollapsed ? 'Dashboard Widgets' : ''}
            className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} px-3 py-2 mb-1 text-sm font-medium rounded-lg transition-all ${
              isActive(TENANT_PATHS.SETTINGS_DASHBOARD_WIDGETS)
                ? 'bg-white text-blue-800'
                : 'text-blue-100 hover:bg-blue-700 hover:text-white'
            }`}
          >
            <div className={`flex items-center ${isCollapsed ? 'justify-center' : ''}`}>
              <SlidersHorizontal className={`h-5 w-5 flex-shrink-0 ${!isCollapsed ? 'mr-3' : ''}`} />
              {!isCollapsed && <span>Dashboard Widgets</span>}
            </div>
          </Link>
        )}
        {navReady && isOrgAdmin && (
        <Link
          to={TENANT_PATHS.SETTINGS_AUDIT_YEARS}
          onClick={(e) => onNavigateFromMateriality(e, TENANT_PATHS.SETTINGS_AUDIT_YEARS)}
          title={isCollapsed ? 'Audit Years' : ''}
          className={`flex items-center ${isCollapsed ? 'justify-center mt-6' : 'justify-between'} px-3 py-2 mb-1 text-sm font-medium rounded-lg transition-all ${
            isActive(TENANT_PATHS.SETTINGS_AUDIT_YEARS)
              ? 'bg-white text-blue-800'
              : 'text-blue-100 hover:bg-blue-700 hover:text-white'
          }`}
        >
          <div className={`flex items-center ${isCollapsed ? 'justify-center' : ''}`}>
            <Settings className={`h-5 w-5 flex-shrink-0 ${!isCollapsed ? 'mr-3' : ''}`} />
            {!isCollapsed && <span>Audit Years</span>}
          </div>
        </Link>
        )}

        {/* AI Activity — org_admin only, and only when this org has integrations */}
        {navReady && isOrgAdmin && orgHasAIIntegration && (
          <Link
            to={TENANT_PATHS.AI_ACTIVITY}
            onClick={(e) => onNavigateFromMateriality(e, TENANT_PATHS.AI_ACTIVITY)}
            title={isCollapsed ? 'AI Activity' : ''}
            className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} px-3 py-2 mb-1 text-sm font-medium rounded-lg transition-all ${
              isActive(TENANT_PATHS.AI_ACTIVITY)
                ? 'bg-white text-blue-800'
                : 'text-blue-100 hover:bg-blue-700 hover:text-white'
            }`}
          >
            <div className={`flex items-center ${isCollapsed ? 'justify-center relative' : ''}`}>
              <Bot className={`h-5 w-5 flex-shrink-0 ${!isCollapsed ? 'mr-3' : ''}`} />
              {!isCollapsed && <span>AI Activity</span>}
              {/* Red dot badge when collapsed */}
              {isCollapsed && aiUnreadCount > 0 && (
                <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-red-500" />
              )}
            </div>
            {/* Pill badge when expanded */}
            {!isCollapsed && aiUnreadCount > 0 && (
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                isActive(TENANT_PATHS.AI_ACTIVITY) ? 'bg-red-100 text-red-700' : 'bg-red-500 text-white'
              }`}>
                {aiUnreadCount > 99 ? '99+' : aiUnreadCount}
              </span>
            )}
          </Link>
        )}
      </nav>

      {/* User Profile Section */}
      <div className="border-t border-blue-700">
        <div className={`${isCollapsed ? 'px-0' : 'p-4'}`}>
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className={`flex items-center ${isCollapsed ? 'justify-center w-full py-4' : 'w-full p-2'} rounded-lg hover:bg-blue-700 transition-colors`}
            >
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 flex-shrink-0">
                <span className="text-white font-semibold text-sm">
                  {getUserInitial()}
                </span>
              </div>
              {!isCollapsed && (
                <div className="ml-3 flex-1 text-left">
                  <p className="text-sm font-medium text-white">{user?.username || 'John Doe'}</p>
                  <p className="text-xs text-blue-200">SOX Administrator</p>
                </div>
              )}
            </button>
            
            {/* Dropdown menu */}
            {showDropdown && (
              <div className={`absolute bottom-full mb-2 ${isCollapsed ? 'left-0' : 'left-0'} ${isCollapsed ? 'w-48' : 'w-full'} bg-white rounded-lg shadow-lg py-1 z-50 border border-gray-200`}>
                <div className="px-4 py-2 border-b border-gray-200">
                  <p className="text-sm font-medium text-gray-900">{user?.username}</p>
                  <p className="text-xs text-gray-500">Signed in</p>
                </div>
                <button
                  type="button"
                  onClick={handleSignOutClick}
                  className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Sign out</span>
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Collapse/Expand Arrow Button */}
        <div className="border-t border-blue-700 px-4 py-3">
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className={`flex items-center justify-center w-full py-2 rounded-lg hover:bg-blue-700 transition-all duration-300 group`}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <ChevronRight className="h-5 w-5 text-blue-200 group-hover:text-white transition-colors" />
            ) : (
              <ChevronLeft className="h-5 w-5 text-blue-200 group-hover:text-white transition-colors" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Sidebar;